/**
 * Split a file size into contiguous byte ranges, one per chunk.
 *
 * Exported so the arithmetic can be tested directly: getting this wrong
 * truncates uploads silently, with nothing downstream noticing. The ranges
 * must always cover [0, fileSize) exactly and number at most numberOfChunks,
 * because the function hands out exactly that many signed upload URLs.
 *
 * @param {number} fileSize
 * @param {number} numberOfChunks
 * @returns {Array<{start: number, end: number}>}
 */
function chunkRanges(fileSize, numberOfChunks) {
 var ranges = [];
 if (!fileSize || fileSize <= 0 || !numberOfChunks || numberOfChunks <= 0) {
  return ranges;
 }
 var chunkSize = Math.ceil(fileSize / numberOfChunks);
 var start = 0;
 while (start < fileSize && ranges.length < numberOfChunks) {
  var end = Math.min(start + chunkSize, fileSize);
  ranges.push({ start: start, end: end });
  start = end;
 }
 return ranges;
}

/**
 * @param {String} url - The url to the cloud function
 * @return {Upload} Upload - the returned function
 */
function Uploader(url) {
 //Just to remove the trailing slash if there is one...
 var baseUrl = url.replace(/^(.+?)\/*?$/, "$1");

 var fileInterface = function(file, id, chunks) {
  let numberOfChunks = chunks || 32;
  var parts = [];
  // Ranges computed up front by chunkRanges, which covers the whole file.
  //
  // This used to be `(file.size / numberOfChunks - 1) >> 0` with the last
  // chunk ending at numberOfChunks * chunkSize, so every upload lost the
  // remainder off the tail — silently, since the composed object was simply
  // shorter than the file the user picked and still marked uploaded.
  // Observed on a real upload as 115164351 bytes in, 115164344 out.
  var ranges = chunkRanges(file.size, numberOfChunks);
  var index = 1;
  var cursor = 0;
  this.getSegment = function() {
   var range = ranges[cursor];
   // No range left: hand back an empty blob, which is the "no more data"
   // signal the workers already act on.
   let chunk = range ? file.slice(range.start, range.end) : file.slice(0, 0);
   let filename = id + "-part-" + index;
   if (chunk.size > 0) {
    parts.push(filename);
   }

   cursor++;
   index++;
   return { chunk: chunk, filename: filename, index: index - 1 };
  };
  this.getParts = function() {
   return parts;
  };
 };

 function createFileWorker() {
  var fn = function(e) {
   var data = e.data;
   var url = data.url;
   var filename = data.filename;
   var index = data.index;
   // Guard against firing both a state-change and an onerror for the same
   // request — some browsers/proxies signal both on a CORS or network fault.
   var sent = false;

   var xhr = new XMLHttpRequest();
   xhr.open("PUT", url, true);
   xhr.onreadystatechange = function() {
    if (sent) return;
    if (xhr.readyState === XMLHttpRequest.DONE) {
     if (xhr.status === 200) {
      sent = true;
      postMessage({
       msg: "uploaded",
       filename: filename,
       transfered: data.chunk.size,
       index: index,
       response: this.responseText
      });
     } else if (xhr.status !== 0) {
      // Anything non-200 that reached the client is a real error.
      // status 0 is left to xhr.onerror below (network / CORS blocked).
      sent = true;
      postMessage({
       msg: "error",
       filename: filename,
       status: xhr.status,
       response: this.responseText,
       chunk: data.chunk
      });
     }
    }
   };
   xhr.onerror = function() {
    if (sent) return;
    sent = true;
    postMessage({
     msg: "error",
     filename: filename,
     status: 0,
     response: "Network or CORS error",
     chunk: data.chunk
    });
   };
   xhr.upload.onprogress = function(e) {
    postMessage({
     msg: "progress",
     index: index,
     transfered: e.loaded,
     bytes: data.chunk.size
    });
   };

   xhr.send(data.chunk);
  };

  var blob = new Blob(["self.onmessage = ", fn.toString()], {
   type: "text/javascript"
  });
  var url = URL.createObjectURL(blob);
  return new Worker(url);
 }

 /**
  * @function Upload
  * @param {File} file - The file to upload
  * @param {Object} userData - Object with uid and bcid
  * @param {Function} onProgress - Function that gets called on progress
  * @param {Function} onComplete - Function that gets called on complete
  * @param {Function} onError - Optional. Called on any hard failure with { stage, status, response, filename? }.
  *                             stage is one of "register" | "chunk" | "manifest".
  * @param {Object} context - Optional. Stored verbatim on the `uploads` doc so an
  *                           upload can be traced back to what it was for (which
  *                           parent video, which sign row). The GCF persists the
  *                           whole register payload, so no GCF change is needed.
  */
 function Upload(file, userData, onProgress, onComplete, onError, context) {
  var progressFunction =
   onProgress ||
   function(p) {
    console.info(
     "Uploaded " + p.uploaded + " of " + p.total + ". " + p.percent + "%"
    );
   };
  var completeFunction =
   onComplete ||
   function(c) {
    console.info(c);
   };
  var errorFunction =
   onError ||
   function(err) {
    console.error("Uploader error:", err);
   };
  // Fire onError at most once — a single fault often triggers multiple signals
  // (state-change + onerror on the same xhr, or all-chunks-error following one
  // network drop) and we don't want to spam the caller.
  var errored = false;
  // aborted is user-initiated cancel. Silences both complete and error paths
  // so a chunk that finishes mid-cancel doesn't fire stale side effects.
  // Server-side cleanup of orphaned chunks is a separate concern — see the
  // uploader GCF TODO.
  var aborted = false;
  var activeXhrs = [];
  var activeWorkers = [];
  var raiseError = function(payload) {
   if (errored || aborted) return;
   errored = true;
   errorFunction(payload);
  };
  var raiseComplete = function(payload) {
   if (aborted) return;
   completeFunction(payload);
  };
  function registerUpload() {
   let fileObject = { name: file.name, size: file.size, mime: file.type };
   let postData = { user: userData, sourceFile: fileObject };
   if (context) {
    postData.context = context;
   }

   let xhr = new XMLHttpRequest();
   activeXhrs.push(xhr);
   xhr.open("POST", baseUrl + "/register", true);
   xhr.onreadystatechange = function() {
    if (aborted) return;
    if (xhr.readyState === XMLHttpRequest.DONE) {
     if (xhr.status === 200) {
      doUpload(xhr.responseText);
     } else {
      raiseError({
       stage: "register",
       status: xhr.status,
       response: xhr.responseText || ""
      });
     }
    }
   };
   xhr.onerror = function() {
    if (aborted) return;
    raiseError({
     stage: "register",
     status: 0,
     response: "Network error registering upload"
    });
   };
   xhr.send(JSON.stringify(postData));
  }
  function doUpload(response) {
   var uploadData = JSON.parse(response);
   console.log(uploadData);
   var numberOfChunks = uploadData.urls.length;
   console.info("Will upload " + file.name + " with the id: " + uploadData.id);
   // console.info(file.size);
   var fileIO = new fileInterface(file, uploadData.id, numberOfChunks);
   var workers = 0;

   var progress = {
    total: file.size,
    progress: new Array(numberOfChunks).fill(0),
    updateProgress: function(index, value) {
     this.progress[index] = value;
     this.logProgress();
    },
    logProgress: function() {
     let uploaded = this.progress.reduce(function(a, b) {
      return a + b;
     }, 0);
     let percent = ((uploaded / this.total) * 100).toFixed(2);

     progressFunction({
      uploaded: uploaded,
      total: this.total,
      percent: percent
     });
     //console.info(uploaded + ' / ' + this.total);
    }
   };

   if (aborted) return;
   // How many chunks actually carried bytes — see the manifest build below.
   var dispatchedParts = 0;
   for (var i = 0; i < numberOfChunks; i++) {
    workers++;
    var worker = createFileWorker();
    activeWorkers.push(worker);
    worker.number = workers;
    worker.id = i;
    worker.onmessage = function(e) {
     if (aborted) return;
     var data = e.data;
     switch (data.msg) {
      case "uploaded":
       progress.updateProgress(data.index, data.transfered);
       workers--;
       this.terminate();
       // Skip finalize if any chunk has already errored — the manifest would
       // reference a chunk that never landed in GCS.
       if (workers === 0 && !errored) {
        // Only the parts that were actually dispatched. Rounding the chunk
        // size up can leave a trailing empty chunk for very small files, and
        // naming it in the manifest would ask the function to compose an
        // object that was never written.
        let filenames = [];
        for (let i = 0; i < dispatchedParts; i++) {
         filenames.push(uploadData.urls[i].file);
        }
        finalizeUpload(uploadData.id, filenames);
       }
       break;
      case "error":
       workers--;
       this.terminate();
       raiseError({
        stage: "chunk",
        filename: data.filename,
        status: data.status,
        response: data.response
       });
       break;
      case "progress":
       progress.updateProgress(data.index, data.transfered);
       break;
      default:
       break;
     }
    };
    worker.onerror = function(e) {
     console.error(e);
    };
    var segment = fileIO.getSegment();
    if (segment.chunk.size > 0) {
     dispatchedParts++;
     worker.postMessage({
      chunk: segment.chunk,
      filename: segment.filename,
      url: uploadData.urls[i].url,
      index: segment.index
     });
    } else {
     workers--;
     worker.terminate();
    }
   }
  }
  function finalizeUpload(id, parts) {
   let fileObject = {
    name: file.name,
    size: file.size,
    mime: file.type,
    parts: parts,
    id: id
   };
   let xhr = new XMLHttpRequest();
   activeXhrs.push(xhr);
   xhr.open("POST", baseUrl + "/manifest", true);
   xhr.onreadystatechange = function() {
    if (aborted) return;
    if (xhr.readyState === XMLHttpRequest.DONE) {
     if (xhr.status === 200) {
      raiseComplete(JSON.parse(xhr.responseText));
     } else {
      raiseError({
       stage: "manifest",
       status: xhr.status,
       response: xhr.responseText || ""
      });
     }
    }
   };
   xhr.onerror = function() {
    if (aborted) return;
    raiseError({
     stage: "manifest",
     status: 0,
     response: "Network error submitting manifest"
    });
   };
   xhr.send(JSON.stringify(fileObject));
  }
  registerUpload();

  return {
   cancel: function() {
    if (aborted) return;
    aborted = true;
    // Terminate all workers (kills any in-flight chunk PUTs)
    for (var i = 0; i < activeWorkers.length; i++) {
     try { activeWorkers[i].terminate(); } catch (e) {}
    }
    activeWorkers.length = 0;
    // Abort register/manifest XHRs that are still pending
    for (var j = 0; j < activeXhrs.length; j++) {
     try { activeXhrs[j].abort(); } catch (e) {}
    }
    activeXhrs.length = 0;
   }
  };
 }
 return Upload;
}

export { Uploader, chunkRanges };
